import {gql} from '@apollo/client';
import {Color} from '@benchling/nucleus-ui';
import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {
  ResolvedAttachmentGraphQL,
  ResolvedAttachmentInput,
  SimplePolymerGraphQL,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {BioentityChip} from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';
import Modal from 'ui-library/modals/Modal';

import {
  ResolvedAttachmentImageQuery,
  ResolvedAttachmentImageQueryVariables,
} from './__generated/ResolvedAttachmentImageQuery';

type ChemicalStructureImage =
  | NonNullable<ResolvedAttachmentGraphQL['chemicalStructureImage']>
  | ResolvedAttachmentImageQuery['moleculeImage'];
type PolymersGraphQL = ResolvedAttachmentGraphQL['polymers'][number] | SimplePolymerGraphQL;

interface ResolvedAttachmentSummaryChipProps {
  chemicalStructureImage: ChemicalStructureImage;
  polymers: Array<PolymersGraphQL>;
  displayName?: string;
}

export function ResolvedAttachmentSummaryChip({
  displayName,
  chemicalStructureImage,
  polymers,
}: ResolvedAttachmentSummaryChipProps): JSX.Element {
  return (
    <div className={css(styles.resolvedAttachmentWrapper)}>
      <div
        className={css(styles.resolvedAttachmentSummary)}
        {...getTestProps({
          component: 'ResolvedAttachmentSummary',
          element: 'conjugateSummary',
          key: displayName,
        })}
        data-amp-safe={false}
      >
        <div className={css(styles.conjugateSummary)}>
          <div>
            {chemicalStructureImage.__typename !== 'ErrorNode' &&
              chemicalStructureImage.base64Svg != null && (
                <img
                  className={css(styles.resolvedAttachmentImage)}
                  src={`data:image/svg+xml;base64,${chemicalStructureImage.base64Svg}`}
                />
              )}
            {chemicalStructureImage.__typename !== 'SVGNode' && chemicalStructureImage.message != null && (
              <div>{chemicalStructureImage.message}</div>
            )}
          </div>
        </div>
        <div className={css(styles.resolveAttachmentComponents)}>
          {polymers.map((polymer) => (
            <div key={polymer.id} className={css(styles.resolvedAttachmentComponentChip)}>
              <BioentityChip
                id={
                  !(
                    polymer.entity.__typename === 'MoleculeNode' ||
                    polymer.entity.__typename === 'SequenceNode' ||
                    polymer.entity.__typename === 'AaSequenceNode'
                  )
                    ? null
                    : polymer.entity.id
                }
                size={ChipSize.SMALL}
                validationExtras={null}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export interface ResolvedAttachmentSummaryProps {
  resolvedAttachment: ResolvedAttachmentGraphQL;
}

export default function ResolvedAttachmentSummary({
  resolvedAttachment,
}: ResolvedAttachmentSummaryProps): JSX.Element | null {
  if (resolvedAttachment.chemicalStructureImage == null) {
    return null;
  }

  return (
    <ResolvedAttachmentSummaryChip
      chemicalStructureImage={resolvedAttachment.chemicalStructureImage}
      polymers={resolvedAttachment.polymers}
      displayName={resolvedAttachment.displayName}
    />
  );
}

export const RESOLVED_ATTACHMENT_IMAGE_QUERY = gql`
  query ResolvedAttachmentImageQuery($mol: StrictString!) @skipAudit @engComponent(name: CHEMISTRY) {
    moleculeImage(mol: $mol) {
      ... on SVGNode {
        base64Svg
      }
      ... on ErrorNode {
        message
      }
    }
  }
`;

export interface ComplexPolymerDragAndDropRemoveAttachmentWarningModalProps {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  simplePolymers: Array<SimplePolymerGraphQL>;
  resolvedAttachment: ResolvedAttachmentInput;
}

export function ComplexPolymerDragAndDropRemoveAttachmentWarningModal(
  props: ComplexPolymerDragAndDropRemoveAttachmentWarningModalProps
): JSX.Element {
  const buttons = [
    {
      text: 'Cancel',
      disabled: false,
      submit: false,
      onClick: props.onRequestClose,
      style: ButtonStyle.TERTIARY_GRAY,
    },
    {
      text: 'Detach',
      disabled: false,
      submit: true,
      style: ButtonStyle.NEGATIVE,
    },
  ];

  const polymerRefsToDelete = new Set(
    props.resolvedAttachment.connections_involved.flatMap((connection) => [
      connection.source.polymer_ref,
      connection.target.polymer_ref,
    ])
  );

  const polymersToDelete = props.simplePolymers.filter((polymer) =>
    polymerRefsToDelete.has(polymer.helmReference)
  );

  return (
    <Modal
      title="Detach conjugate"
      mediumLarge={true}
      buttons={buttons}
      onRequestClose={props.onRequestClose}
      scrollable={false}
      onRequestSubmit={props.onRequestSubmit}
    >
      <BenchlingQuery<ResolvedAttachmentImageQuery, ResolvedAttachmentImageQueryVariables>
        query={RESOLVED_ATTACHMENT_IMAGE_QUERY}
        renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
        variables={{mol: props.resolvedAttachment.molfile}}
      >
        {(data) => {
          const {moleculeImage} = data;
          return (
            <div className={css(styles.bodyContainer)}>
              <span className={css(styles.warningText)}>
                Deleting this connection will detach the associated conjugate and remove all connections
                between components.
              </span>
              <span className={css(styles.warningText)}>Do you want to proceed?</span>
              <div className={css(styles.detachPreview)}>
                <ResolvedAttachmentSummaryChip
                  chemicalStructureImage={moleculeImage}
                  polymers={polymersToDelete}
                />
              </div>
            </div>
          );
        }}
      </BenchlingQuery>
    </Modal>
  );
}

const styles = StyleSheet.create({
  displayNameChip: {
    display: 'inline-block',
    pointerEvents: 'none',
  },
  conjugateSummary: {
    display: 'inline-block',
    padding: '4px',
    border: `1px solid ${Color.GRAY_200}`,
    borderRadius: '4px',
    margin: '4px',
    background: Color.WHITE,
    position: 'relative',
  },
  conjugateSummaryHover: {
    ':hover': {
      background: Color.GRAY_200,
    },
  },
  resolvedAttachmentSummary: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
    padding: '8px',
    background: Color.GRAY_50,
    width: 'fit-content',
    marginBottom: '20px',
  },
  resolveAttachmentComponents: {
    display: 'flex',
    flexDirection: 'column',
    width: 'max-content',
  },
  resolvedAttachmentComponentChip: {
    padding: '5px',
  },
  resolvedAttachmentWrapper: {
    paddingRight: '20px',
    width: 'fit-content',
  },
  resolvedAttachmentImage: {
    width: 'auto',
    zIndex: 1,
    maxWidth: '500px',
    maxHeight: '200px',
  },
  attachmentIcons: {
    position: 'absolute',
    top: '41%',
    left: '45%',
    zIndex: 2,
  },
  bodyContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  detachPreview: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: '10px',
  },
  warningText: {
    fontSize: '14px',
  },
});
